
import { faEye } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogProps, Link, MenuItem, Tooltip, Typography, useTheme } from '@mui/material';
import { IFormActions, ValidateOn } from '@ngt/forms-core';
import { DatePicker, FileUpload, Form, FormDefinitionContext, FormSubmitButton, GroupedField, GroupedTable, GroupedTableRow, IFileUpload, IFormValidationError, IGroupedTableCellProps, IGroupedTableHeaderProps, Input, LookupsContext, Select, Selector, Text, ToggleButtonGroup, useFormLabels, useFormLookups } from '@ngt/opms';
import { DateTime } from 'luxon';
import { FC, MouseEvent, useCallback, useContext, useMemo, useState } from 'react';

import { File as OpmsFile, Patient, SaConsentStatus, ServicesAustraliaConsent, ServicesAustraliaConsentHistory } from '../../../api/dtos';
import { AppSettings } from '../../../AppSettings';
import useGetServicesAustraliaConsentHistory from '../../../hooks/data/useGetServicesAustraliaConsentHistory';
import useSaConsentForm from '../../../hooks/form/useSaConsentForm';
import GroupedExpandableField from '../../forms/fields/GroupedExpandableField';


interface ISaConsentDialogProps extends DialogProps {
    patient: Patient | undefined;
    canComplete?: boolean;
    onCancel: () => void;
    onReview: (patient: Patient) => void;
}


const reviewTableHeaders: IGroupedTableHeaderProps[] = [
    {
        columnName: 'document',
        content: 'Document',
        TableCellProps: {
            sx: {
                width: '25%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'version',
        content: 'Version',
        TableCellProps: {
            sx: {
                width: '25%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'date',
        content: 'Date',
        TableCellProps: {
            sx: {
                width: '24.2%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'versionTmd',
        content: 'Current Version in TMD',
        TableCellProps: {
            sx: {
                width: '25.8%',
                minWidth: 0
            }
        }
    }
];

const consentUploadHeaders: IGroupedTableHeaderProps[] = [
    {
        columnName: 'name',
        content: 'Name',
        TableCellProps: {
            sx: {
                width: '25%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'fileType',
        content: 'File Type',
        TableCellProps: {
            sx: {
                width: '25%',
                minWidth: 0
            }
        }
    },
    {
        columnName: 'file',
        content: 'File',
        TableCellProps: {
            sx: {
                width: '25%',
                minWidth: 0
            }
        }
    }
];

const SaConsentDialog: FC<ISaConsentDialogProps> = ({
    patient,
    canComplete,
    onCancel,
    onReview,
    ...rest
}) => {
    const theme = useTheme();

    const { data: formDefinition } = useContext(FormDefinitionContext)
    const { data: lookups } = useContext(LookupsContext);

    const formLookups = useFormLookups(lookups);
    const labels = useFormLabels(formDefinition);

    const {
        formInitial,
        loading,
        validate,
        validating,
        allowSubmit,
        handleSubmit
    } = useSaConsentForm({ 
        patient
    });

    const {
        data: consentHistory
    } = useGetServicesAustraliaConsentHistory(patient);

    const [theCurrentForm, setTheCurrentForm] = useState<ServicesAustraliaConsent | undefined>();

    const dropDownRow = useMemo(() => (
        consentHistory?.map((d, i) => {
            return (
                <MenuItem key={i} value={i}>
                    {DateTime.fromISO(d.modifiedDate!).toFormat("ff")}
                </MenuItem>
            )
        })
    ), [consentHistory]);

    const saConsentStatus = useMemo(
        () => formInitial?.saConsentStatus, 
        [formInitial?.saConsentStatus]
    );

    // const [currentFormHistoryId, setCurrentFormHistoryId] = useState<string | undefined>(undefined);

    const reducedConsentHistory = useMemo(() => {
        if (!consentHistory || consentHistory.length === 0) {
            return [];
        }

        return consentHistory.reduce<ServicesAustraliaConsentHistory[]>(
            (state, currentItem) => {
                state.push(currentItem);

                return state;
            },
            []
        );
    }, [consentHistory]);

    const existingLink = (value?: IFileUpload) => {
        const file = value as OpmsFile;
    
        return `/opms/file/view/${file?.id}`;
    }

    const existingDownloadLink = (value?: IFileUpload) => {
        const file = value as OpmsFile;
    
        return `/opms/file/download/${file?.id}`;
    }

    const handleRejectClick = useCallback(async (_event: MouseEvent<HTMLButtonElement>, formActions: IFormActions<ServicesAustraliaConsent, IFormValidationError>) => {
        formActions?.setFieldValue("saConsentStatus", SaConsentStatus.Pending);
        formActions?.setFieldValue("isHistoryEntry", true);
        formActions?.submit();
    }, []);

    const handleSaveAndReturn = useCallback(async (_event: MouseEvent<HTMLButtonElement>, formActions: IFormActions<ServicesAustraliaConsent, IFormValidationError>) => {
        formActions?.setFieldValue("saveAndReturn", true);
        formActions?.setFieldValue("saConsentStatus", SaConsentStatus.PendingReview);
        formActions?.setFieldValue("isHistoryEntry", false);
        formActions?.submit();
    }, [])

    const handleCompleteClick = useCallback(async (_event: MouseEvent<HTMLButtonElement>, formActions: IFormActions<ServicesAustraliaConsent, IFormValidationError>) => {
        formActions?.setFieldValue("saConsentStatus", SaConsentStatus.Complete);
        formActions?.setFieldValue("isHistoryEntry", true);
        formActions?.submit();
    }, []);

    const handleConfirmAndProceedClick = useCallback(async (_event: MouseEvent<HTMLButtonElement>, formActions: IFormActions<ServicesAustraliaConsent, IFormValidationError>) => {
        formActions?.setFieldValue("saConsentStatus", SaConsentStatus.PendingReview);
        formActions?.setFieldValue("isHistoryEntry", false);
        formActions?.submit();
    }, []);
    
    const consentUploadRow: IGroupedTableCellProps[] = useMemo(() => {
        const ddx = formInitial
            .sourceDocuments
            ?.findIndex(x => x.fileTypeId === 3);

        return [
            {
                columnName: 'name',
                propertyPath: `sourceDocuments[${ddx}].name`,
                content: (
                    <Input component={Text} />
                )
            },
            {
                columnName: 'fileType',
                propertyPath: `sourceDocuments[${ddx}].fileTypeId`,
                content: (
                    <Input 
                        component={Select}
                        disabled
                    />
                )
            },
            {
                columnName: 'file',
                propertyPath: `sourceDocuments[${ddx}].file`,
                content: (
                    <Input
                        component={FileUpload}
                        existingLink={existingLink}
                        existingDownloadLink={existingDownloadLink}
                    />
                )
            }
        ];
    }, [formInitial]);

    const consentRow: IGroupedTableCellProps[] = useMemo(() => ([
        {
            columnName: "document",
            content: (
                
                formInitial?.saConsentStatus != SaConsentStatus.Complete ?
                <Link
                    style={{ cursor: "pointer" }}
                    href={`/pdfviewer/file/${
                        formInitial?.sourceDocuments?.find(
                            (x) => x.fileTypeId === 3
                        )?.file?.id ?? "undefined"
                    }`}
                    target="_blank"
                >
                    {
                        !theCurrentForm && saConsentStatus !== SaConsentStatus.Processed ?
                            <Typography>
                                {
                                    formInitial?.sourceDocuments?.find(
                                        (x) => x.fileTypeId === 3
                                    )?.name
                                }
                                <FontAwesomeIcon
                                    icon={faEye}
                                    fixedWidth
                                    style={{ marginLeft: "0.5rem" }}
                                />
                                
                            </Typography>
                            :
                            <></>
                    }
                </Link> :
                !theCurrentForm && saConsentStatus !== SaConsentStatus.Processed ?
                    <Typography>
                        {
                            formInitial?.sourceDocuments?.find(
                                (x) => x.fileTypeId === 3
                            )?.name
                        }
                    </Typography>
                    :
                    <></>
            ),
            fieldName: "document",
        },
        {
            columnName: "version",
            propertyPath: "consentFormVersion",
            content: (
                <Tooltip 
                    title="SA consent should be the latest version"
                >
                    <Input 
                        component={Text}
                        disabled={saConsentStatus === SaConsentStatus.Complete || saConsentStatus === SaConsentStatus.Processed}
                        
                    />
                </Tooltip>
            )
        },
        {
            columnName: "date",
            propertyPath: "consentFormDate",
            content: (
                <Input 
                    component={DatePicker}
                    disabled={saConsentStatus === SaConsentStatus.Complete || saConsentStatus === SaConsentStatus.Processed}
                />
            )
        },
        {
            columnName: "versionTmd",
            propertyPath: "currentTmdConsentFormVersion",
            content: (
                <Input 
                    component={ToggleButtonGroup}
                    disabled={saConsentStatus === SaConsentStatus.Complete || saConsentStatus === SaConsentStatus.Processed}
                />
            )
        }
    ]), [formInitial, saConsentStatus, theCurrentForm]);

    return (
        <Dialog
            {...rest}
            fullWidth
            maxWidth="xl"
            scroll="body"
        >
            {
                loading || validating ? 
                    <Box display="flex" width="100%">
                        <CircularProgress sx={{ margin: '16px auto' }} /> 
                    </Box> :
                    <Form
                        lookups={formLookups}
                        labels={labels}
                        debug={AppSettings.debug}
                        initialValue={theCurrentForm ?? formInitial}
                        allowSubmit={allowSubmit}
                        onSubmit={handleSubmit}
                        validateOn={ValidateOn.onChange}
                        onValidate={validate}
                        readOnly={!!theCurrentForm}
                    >
                        <DialogContent sx={{ padding: 0 }}>
                            {
                                saConsentStatus === SaConsentStatus.Pending &&
                                <>
                                    <Typography
                                        variant="h4"
                                        color="primary"
                                        sx={{ 
                                            color: theme.palette.secondary.main,
                                            padding: theme.spacing(2.5, 3, 0),
                                            fontSize: '1.25rem !important',
                                            fontWeight: 500,
                                            marginBottom: '16px'
                                        }}
                                    >
                                        Service Australia Consent Review - {patient?.studyNumber}
                                    </Typography>

                                    <GroupedTable 
                                        headers={consentUploadHeaders}
                                    >
                                        <GroupedTableRow cells={consentUploadRow} />
                                    </GroupedTable>
                                </>
                            }
                            {
                                saConsentStatus !== SaConsentStatus.Pending &&
                                <>
                                    <Typography
                                        variant="h4"
                                        color="primary"
                                        sx={{ 
                                            color: theme.palette.secondary.main,
                                            padding: theme.spacing(2.5, 3, 0),
                                            fontSize: '1.25rem !important',
                                            fontWeight: 500,
                                            marginBottom: '16px'
                                        }}
                                    >
                                        Service Australia Consent Review - {patient?.studyNumber}
                                    </Typography>

                                    <Box padding={theme.spacing(2.5, 3, 0)}>
                                        <Selector
                                            label="History" 
                                            selection={
                                                theCurrentForm
                                                    ? reducedConsentHistory
                                                        .indexOf(theCurrentForm)
                                                        .toString()
                                                    : "New"
                                            }
                                            inputId={
                                                theCurrentForm
                                                    ? reducedConsentHistory
                                                        .indexOf(theCurrentForm)
                                                        .toString()
                                                    : "New"
                                            }
                                            onSelectionChanged={(code) => {
                                                //if there is nothing in the selection OR it == New then set form to not a history
                                                if (
                                                    code === "" ||
                                                    code === null ||
                                                    code === undefined ||
                                                    code === "New"
                                                ) {
                                                    setTheCurrentForm(undefined);
                                                }
                                                // else set the form to the history array of whichever dropdown is selected
                                                else {
                                                    setTheCurrentForm(reducedConsentHistory[+code] as ServicesAustraliaConsent);
                                                }
                                            }}
                                        >
                                            {
                                                (formInitial.saConsentStatus === SaConsentStatus.Complete || formInitial.saConsentStatus === SaConsentStatus.Processed) &&
                                                formInitial.modifiedDate ? (
                                                    <MenuItem
                                                        key={formInitial.modifiedDate}
                                                        value={"New"}
                                                    >
                                                        {DateTime.fromISO(
                                                            formInitial.modifiedDate
                                                        ).toFormat("ff")}
                                                    </MenuItem>
                                                ) : (
                                                    <MenuItem key={"0"} value={"New"}>
                                                        New
                                                    </MenuItem>
                                                )
                                            }

                                            {dropDownRow}
                                        </Selector>
                                    </Box>

                                    <GroupedTable 
                                        headers={reviewTableHeaders}
                                        stickyHeader={true}
                                    >
                                        <GroupedTableRow cells={consentRow} />
                                    </GroupedTable>

                                    <GroupedField 
                                        propertyPath='participantSignedCorrectVersion'
                                        component={ToggleButtonGroup}
                                        disabled={saConsentStatus === SaConsentStatus.Complete || saConsentStatus === SaConsentStatus.Processed}
                                    />

                                    <GroupedField 
                                        propertyPath='consentFullyCompleted'
                                        component={ToggleButtonGroup}
                                        disabled={saConsentStatus === SaConsentStatus.Complete || saConsentStatus === SaConsentStatus.Processed}
                                    />

                                    <GroupedField 
                                        propertyPath='correctionsSigned'
                                        component={ToggleButtonGroup}
                                        disabled={saConsentStatus === SaConsentStatus.Complete || saConsentStatus === SaConsentStatus.Processed}
                                    />

                                    <GroupedField 
                                        propertyPath='witnessIdentityConfirmed'
                                        component={ToggleButtonGroup}
                                        disabled={saConsentStatus === SaConsentStatus.Complete || saConsentStatus === SaConsentStatus.Processed}
                                    />

                                    <Tooltip 
                                        title="This comment will be included in the email sent to sites if corrections are required."
                                    >
                                        <GroupedExpandableField 
                                            propertyPath="additionalComments"
                                            expandableContent={
                                                <Typography
                                                    variant="body2"
                                                >
                                                    NOTE: This comment will be included in the email sent to sites if corrections are required.
                                                </Typography>
                                            }
                                            component={Text}
                                            variant="outlined"
                                            multiline
                                            minRows={2}
                                            disabled={saConsentStatus === SaConsentStatus.Complete || saConsentStatus === SaConsentStatus.Processed}
                                        />
                                    </Tooltip>

                                    <GroupedField 
                                        propertyPath='reviewedBy'
                                        component={Text}
                                        disabled
                                    />

                                    <GroupedField 
                                        propertyPath='reviewedDate'
                                        component={DatePicker}
                                        disabled
                                    />
                                </>
                            }
                        </DialogContent>
                        <DialogActions >
                            
                            {
                                saConsentStatus === SaConsentStatus.Pending && 
                                <>
                                    <Button 
                                        onClick={onCancel} 
                                        color="secondary"
                                    >
                                        Cancel
                                    </Button>
                                    <FormSubmitButton
                                        color="primary"
                                        onClick={handleConfirmAndProceedClick}
                                    >
                                        Confirm and Proceed
                                    </FormSubmitButton>
                                </>
                            }
                            {
                                saConsentStatus === SaConsentStatus.PendingReview && 
                                <>
                                    <Button 
                                        color="secondary"
                                        onClick={onCancel} 
                                    >
                                        Cancel
                                    </Button>
                                    {
                                        !theCurrentForm &&
                                        <>  
                                            <FormSubmitButton
                                                color="primary"
                                                onClick={handleSaveAndReturn}
                                            >
                                                Save and Return
                                            </FormSubmitButton>
                                            {
                                                (canComplete ?? true) &&
                                                <FormSubmitButton
                                                    color="primary"
                                                    onClick={handleCompleteClick}
                                                >
                                                    SDV Complete
                                                </FormSubmitButton>
                                            }
                                            <FormSubmitButton
                                                color="primary"
                                                onClick={handleRejectClick}
                                            >
                                                Consent Reviewed - Updates Required
                                            </FormSubmitButton>
                                        </>
                                    }
                                </>
                            }
                            {
                                (saConsentStatus === SaConsentStatus.Complete || saConsentStatus === SaConsentStatus.Processed) &&
                                <Button 
                                    onClick={onCancel} 
                                    color="secondary"
                                >
                                    Close
                                </Button>
                            }
                        </DialogActions>
                    </Form>
            }
        </Dialog>
    );
}

export default SaConsentDialog;